/*
 * Pure number formatting for money, percentages and chart axes. Money comes in
 * as integer minor units (cents); negatives use the true minus sign (U+2212)
 * so a column of figures lines up.
 */

const MINUS = "−";

function group(whole: number): string {
  return String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** Trim a trailing ".0" from a one-decimal figure. */
function trimZero(fixed: string): string {
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}

/** Cents to "1,234.56"; "−500.00" for a negative; `signDisplay: "always"` adds "+". */
export function formatMinor(
  minor: number,
  opts: { signDisplay?: "auto" | "always" } = {},
): string {
  const rounded = Math.round(minor);
  const abs = Math.abs(rounded);
  const text = `${group(Math.floor(abs / 100))}.${String(abs % 100).padStart(2, "0")}`;
  if (rounded < 0) return `${MINUS}${text}`;
  if (rounded > 0 && opts.signDisplay === "always") return `+${text}`;
  return text;
}

function currencySymbol(currency: string): string {
  if (currency === "USD") return "$";
  try {
    const part = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    return part?.value ?? currency;
  } catch {
    return currency;
  }
}

/** "$6,735", "−$1,599", "$12.9K", "$4.2M": whole dollars under 10,000, else K/M. */
export function formatMoneyCompact(minor: number, currency = "USD"): string {
  const dollars = Math.abs(minor) / 100;
  const symbol = currencySymbol(currency);
  let body: string;
  if (Math.round(dollars) < 10_000) {
    body = `${symbol}${group(Math.round(dollars))}`;
  } else {
    const thousands = trimZero((dollars / 1_000).toFixed(1));
    body =
      dollars >= 1_000_000 || Number(thousands) >= 1_000
        ? `${symbol}${trimZero((dollars / 1_000_000).toFixed(1))}M`
        : `${symbol}${thousands}K`;
  }
  const negative = minor < 0 && body !== `${symbol}0`;
  return negative ? `${MINUS}${body}` : body;
}

/**
 * An axis tick: "$500", "$2.5K", "$10K", "$1.5M". From a thousand up every
 * tick is in K or M, so one axis never mixes "$5,000" with "$10K".
 */
export function formatMoneyTick(minor: number, currency = "USD"): string {
  const dollars = Math.abs(minor) / 100;
  const symbol = currencySymbol(currency);
  const body =
    dollars >= 1_000_000
      ? `${symbol}${trimZero((dollars / 1_000_000).toFixed(1))}M`
      : dollars >= 1_000
        ? `${symbol}${trimZero((dollars / 1_000).toFixed(1))}K`
        : `${symbol}${group(Math.round(dollars))}`;
  return minor < 0 && body !== `${symbol}0` ? `${MINUS}${body}` : body;
}

/** "77%", "+367.6%", "−48.7%", "<1%" for a trace above zero, "—" for no value. */
export function formatPct(
  value: number | null,
  opts: { signed?: boolean; decimals?: number } = {},
): string {
  if (value === null || !Number.isFinite(value)) return "—";
  if (value > 0 && value < 1) return "<1%";
  const fixed = Math.abs(value).toFixed(opts.decimals ?? 1);
  const text = opts.decimals === undefined ? trimZero(fixed) : fixed;
  if (Number(fixed) === 0) return `${text}%`;
  if (value < 0) return `${MINUS}${text}%`;
  return `${opts.signed ? "+" : ""}${text}%`;
}

function niceStep(rough: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / magnitude;
  const factor = [1, 2, 2.5, 5, 10].find((f) => norm <= f + 1e-9) ?? 10;
  return factor * magnitude;
}

/**
 * Round ticks (steps of 1, 2, 2.5 or 5 x 10^n) covering [min, max]. Zero is a
 * tick whenever the domain crosses or touches it; a flat domain gets a range.
 */
export function niceTicks(min: number, max: number, count = 4): number[] {
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  if (lo === hi) {
    if (lo === 0) return [0, 1];
    if (lo > 0) lo = 0;
    else hi = 0;
  }
  const step = niceStep((hi - lo) / Math.max(1, count));
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  const start = Math.floor(lo / step + 1e-9);
  const end = Math.ceil(hi / step - 1e-9);
  const ticks: number[] = [];
  for (let i = start; i <= end; i++) {
    ticks.push(Number((i * step).toFixed(decimals)) + 0);
  }
  return ticks;
}

const isDigit = (code: number) => code >= 48 && code <= 57;

/** A character a provider puts in front of a mask: x, *, •, ·, #, or a space. */
const isMaskMarker = (ch: string) =>
  ch === "x" ||
  ch === "X" ||
  ch === "*" ||
  ch === "•" ||
  ch === "·" ||
  ch === "#" ||
  ch === " ";

/**
 * The part of a provider's mask that identifies the account: its trailing
 * digits ("x4673", "••4673", "4673 " → "4673"), or — for an alphanumeric
 * mask such as Plaid's "12AB" — the mask without its leading markers, so two
 * "Checking" accounts masked 12AB and 34CD stay apart. "" for markers alone.
 */
export function maskDigits(mask: string | null | undefined): string {
  const trimmed = (mask ?? "").trim();
  let start = trimmed.length;
  while (start > 0 && isDigit(trimmed.charCodeAt(start - 1))) start -= 1;
  if (start < trimmed.length) return trimmed.slice(start);
  let lead = 0;
  while (lead < trimmed.length && isMaskMarker(trimmed[lead]!)) lead += 1;
  return trimmed.slice(lead);
}

/** True when the mask tail occurs in `name` as a standalone token ("(4673)", "4673 USD", "••4673", "x4673", "••12AB"). */
function carriesDigits(name: string, digits: string): boolean {
  let from = 0;
  for (;;) {
    const at = name.indexOf(digits, from);
    if (at === -1) return false;
    const before = at === 0 || !isDigit(name.charCodeAt(at - 1));
    const end = at + digits.length;
    const after = end === name.length || !isDigit(name.charCodeAt(end));
    if (before && after) return true;
    from = at + 1;
  }
}

/**
 * THE account label rule, settled 2026-10-07 (review 848): the mask is its
 * trailing digits (Plaid "4673", Mercury "x4673", some feeds "••4673" or
 * "4673 "), and the name carries it when those digits appear anywhere in it
 * as a standalone run ("PG Checking ••4673", "Checking (4673)",
 * "Checking 4673 USD"). "Savings 12345" with mask 1234 keeps its ••1234.
 * A blank name is the fallback ("Account", or "" for just the mask). Every
 * surface in both apps goes through this or `accountMaskSuffix`.
 */
export function accountLabel(
  name: string | null | undefined,
  mask: string | null | undefined,
  fallback = "Account",
): string {
  const base = (name ?? "").trim() || fallback;
  const digits = maskDigits(mask);
  if (!digits) return base;
  if (base && carriesDigits(base, digits)) return base;
  return base ? `${base} ••${digits}` : `••${digits}`;
}

/**
 * The "••1234" tail alone, for a row that sets it in a muted span after the
 * name; null when the name already carries the mask or there is no mask.
 */
export function accountMaskSuffix(
  name: string | null | undefined,
  mask: string | null | undefined,
): string | null {
  const digits = maskDigits(mask);
  if (!digits) return null;
  const base = (name ?? "").trim();
  return base && carriesDigits(base, digits) ? null : `••${digits}`;
}
